/**
 * Garden — the shared world. Two players walk around a painted twilight
 * garden. Plants are glowing orbs aligned to painted features of the
 * backdrop. Tending together unlocks a memory cutscene.
 *
 * Full-bleed canvas, viewport-relative layout, ResponsiveResize safe.
 */
import Phaser from 'phaser';
import { Transport } from '../systems/Transport.js';
import { Audio } from '../systems/AudioManager.js';
import { SFX } from '../systems/SFX.js';
import { LetterManager } from '../systems/LetterManager.js';
import { SeedSystem } from '../systems/SeedSystem.js';
import { PresenceSystem } from '../systems/PresenceSystem.js';
import { GestureSystem } from '../systems/GestureSystem.js';
import { GardenState } from '../systems/GardenState.js';
import { WeatherSystem } from '../systems/WeatherSystem.js';
import { PLANTS } from '../data/plants.js';

export class GardenScene extends Phaser.Scene {
  constructor() { super({ key: 'GardenScene' }); }

  init(data) {
    this.roomCode = data?.roomCode || 'XXXX';
    this.role = data?.role || 'host';
  }

  create() {
    // Reset per-entry state — Phaser reuses scene instances across scene.start()
    this._memoryStarting = false;
    this._tendingPlants = new Map();
    // Persist bloomed-plants list across scene.start() calls (same session)
    if (!this._bloomed) this._bloomed = new Set();
    // Behavior tracking for letter context
    this._sessionStart = Date.now();
    this._plantsTendedThisSession = [];

    // Letter system — connect to live Grandma
    LetterManager.attach(this.roomCode);
    // When a new letter arrives (from our own request OR relay from peer),
    // chime + pulse the mailbox firefly
    LetterManager.on('new_letter', (letter) => this._onNewLetter(letter));

    // Persistent per-room state (bloomed plants, secret plant, tend counts)
    GardenState.load(this.roomCode).then((state) => {
      // Seed local bloomed Set from server state so plants appear gold from load
      if (state.bloomedPlants && state.bloomedPlants.length) {
        for (const id of state.bloomedPlants) this._bloomed.add(id);
        // Re-apply bloom visual to any already-spawned plant orbs
        this._reapplyBloomVisuals();
      }
      // Re-spawn a previously-grown secret plant at its saved position
      if (state.secretPlant && !this._plants.secret_heart) {
        this._secretPlantSpawned = true;
        const x = state.secretPlant.nx * this.scale.width;
        const y = state.secretPlant.ny * this.scale.height;
        this._spawnSecretPlantAt(x, y, /* skipLetter = */ true);
      }
      // Track visit count
      GardenState.increment({ incrementVisit: true });
    });

    // Seed mini-game (spawns between memories)
    this._seedSystem = new SeedSystem(this);
    // Presence layer — trails, bond, togetherness
    this._presenceSystem = new PresenceSystem(this);
    // Gesture layer — drawing + shape recognition
    this._gestureSystem = new GestureSystem(this);
    // Weather layer — rain/fog/firefly swarm
    this._weatherSystem = new WeatherSystem(this);
    this._weatherSystem.load(this.roomCode);
    // Spawn a seed when re-entering the garden after at least 1 memory has been
    // played and the secret plant hasn't appeared yet
    if (this._bloomed.size >= 1 && !this._secretPlantSpawned) {
      this.time.delayedCall(2500, () => {
        if (this.role === 'host' && !this._seedSystem.active && !this._secretPlantSpawned) {
          this._seedSystem.spawn();
        }
      });
    }

    this._layout();

    // Handle viewport resize (device rotation, window resize)
    this.scale.on('resize', this._onResize, this);

    // Audio
    Audio.attach(this);
    Audio.playMusic('ambient_garden', { fadeIn: 2500, volume: 0.38 });

    // Multiplayer transport
    Transport.connect(this.roomCode, this.role);
    this._wireTransport();

    // Fade in
    this.cameras.main.fadeIn(900, 0, 0, 0);
  }

  _onResize() {
    // Full redraw on resize — destroys transient elements but keeps state
    this.tweens.killAll();
    this.children.removeAll(true);
    this._layout();
  }

  update(time, delta) {
    // Tick presence (trails, bond line, togetherness)
    if (this._presenceSystem) this._presenceSystem.update(time, delta);

    if (!this._plants) return;
    for (const plant of Object.values(this._plants)) {
      if (plant.localHoldStart) {
        // Advance local progress
        const elapsed = Date.now() - plant.localHoldStart;
        const newProgress = Math.min(1, elapsed / this.HOLD_DURATION_MS);
        plant.localHoldProgress = newProgress;
        this._updateHoldRings(plant);
        // Send progress update to peer (throttled ~every 120ms)
        plant._progressSendTimer = (plant._progressSendTimer || 0) + delta;
        if (plant._progressSendTimer > 120) {
          plant._progressSendTimer = 0;
          Transport.send('hold_progress', { plantId: plant.def.id, p: newProgress });
        }
        // Check fire
        this._checkDualHold(plant);
      }
    }
  }

  _layout() {
    const w = this.scale.width;
    const h = this.scale.height;

    // === Painted twilight garden — cover mode ===
    if (this.textures.exists('garden_twilight')) {
      const bg = this.add.image(w / 2, h / 2, 'garden_twilight').setDepth(0);
      const scale = Math.max(w / bg.width, h / bg.height);
      bg.setScale(scale);
      this._bgRect = {
        left: bg.x - bg.displayWidth / 2,
        top: bg.y - bg.displayHeight / 2,
        width: bg.displayWidth,
        height: bg.displayHeight,
      };
      // Time-of-day tint: apply a soft color overlay to the painting based on wall-clock hour
      const tint = this._timeOfDayTint();
      bg.setTint(tint.tint);
    } else {
      this._drawProcBg(w, h);
      this._bgRect = { left: 0, top: 0, width: w, height: h };
    }

    // Time-of-day color wash overlay for mood
    const tod = this._timeOfDayTint();
    const vig = this.add.graphics().setDepth(1);
    vig.fillStyle(tod.wash, tod.washAlpha);
    vig.fillRect(0, 0, w, h);

    // === Atmospheric particles ===
    this._spawnFireflies(Math.floor((w * h) / 14000));
    this._spawnDust(Math.floor((w * h) / 10000));

    // === Plants as glowing orbs at painted-feature positions ===
    this._plants = {};
    PLANTS.forEach(def => {
      this._plants[def.id] = this._createPlantOrb(def);
    });

    // === Mailbox — Grandma's letters arrive here ===
    this._createMailbox(w, h);

    // === Players ===
    if (!this._players) this._players = new Map();
    // Reset on resize — recreate local player
    this._players.clear();
    this._myPlayer = this._spawnPlayer(Transport.myId || 'me', this.role === 'host' ? 0xffd54f : 0xff80ab, w * 0.5, h * 0.88);

    // Touch controls — tap walks, drag draws a gesture
    this.input.off('pointerdown');
    this.input.off('pointermove');
    this.input.off('pointerup');
    this.input.on('pointerdown', (pointer) => this._onPointerDown(pointer));
    this.input.on('pointermove', (pointer) => this._onPointerMove(pointer));
    this.input.on('pointerup', (pointer) => this._onPointerUp(pointer));

    // Tend state
    if (!this._tendingPlants) this._tendingPlants = new Map();

    // HUD
    this._buildHUD(w, h);
  }

  // ── Viewport-normalized positioning ─────────────────────────

  _normalizedToScreen(nx, ny) {
    return { x: nx * this.scale.width, y: ny * this.scale.height };
  }

  // ── Time-of-day tint ────────────────────────────────────────
  // Shifts the garden's mood based on when you actually open it.
  // Morning (5-10): warm peach wash, slight yellow tint on bg
  // Midday (10-14): bright blue wash, neutral tint
  // Afternoon (14-18): amber wash, warm tint
  // Dusk (18-21): pink wash, default twilight tint (the painting's native mood)
  // Night (21-5): deep blue wash, cool desaturated tint

  _timeOfDayTint() {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 10) {
      // Morning
      return { tint: 0xffe8c0, wash: 0xffc88a, washAlpha: 0.12 };
    }
    if (hour >= 10 && hour < 14) {
      // Midday — bright
      return { tint: 0xffffff, wash: 0x88ccff, washAlpha: 0.08 };
    }
    if (hour >= 14 && hour < 18) {
      // Afternoon — warm amber
      return { tint: 0xffe0a8, wash: 0xff9868, washAlpha: 0.10 };
    }
    if (hour >= 18 && hour < 21) {
      // Dusk — the native twilight mood (most neutral tint)
      return { tint: 0xffffff, wash: 0x1a0e2e, washAlpha: 0.18 };
    }
    // Night — cool deep blue wash, desaturated
    return { tint: 0x8090b8, wash: 0x0a0e24, washAlpha: 0.35 };
  }

  // ── Plant orb ───────────────────────────────────────────────

  _createPlantOrb(def) {
    const { x, y } = this._normalizedToScreen(def.nx, def.ny);
    const obj = { def, tendingPlayers: new Set(), x, y };

    // If already bloomed this session, use warm gold instead of the plant's color
    const bloomed = this._bloomed?.has(def.id);
    const color = bloomed ? 0xfff4a0 : def.color;

    // Layer 1 — wide outer halo
    obj.halo = this.add.circle(x, y, 38, color, 0.12).setDepth(20);
    this.tweens.add({
      targets: obj.halo,
      scale: { from: 0.9, to: 1.25 },
      alpha: { from: 0.08, to: 0.22 },
      duration: 2400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // Layer 2 — inner glow disk
    obj.glow = this.add.circle(x, y, 18, color, bloomed ? 0.35 : 0.55).setDepth(21);
    this.tweens.add({
      targets: obj.glow, alpha: { from: bloomed ? 0.25 : 0.45, to: bloomed ? 0.5 : 0.75 },
      duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // Layer 3 — bright core
    obj.core = this.add.circle(x, y, 6, 0xfff4cc, bloomed ? 0.7 : 1).setDepth(22);
    this.tweens.add({
      targets: obj.core, scale: { from: 0.85, to: 1.15 },
      duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // Drifting petal particles around the orb
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const r = 18 + Math.random() * 8;
      const petal = this.add.circle(x + Math.cos(angle) * r, y + Math.sin(angle) * r, 1.5, color, 0.9).setDepth(21);
      this.tweens.add({
        targets: petal,
        angle: 360,
        duration: 4000 + Math.random() * 2000,
        repeat: -1,
      });
      // Orbit via custom tween
      let ang = angle;
      const dist = r;
      const orbitEvt = this.time.addEvent({
        delay: 33, loop: true, callback: () => {
          ang += 0.008 + Math.random() * 0.004;
          petal.x = x + Math.cos(ang) * dist;
          petal.y = y + Math.sin(ang) * dist;
        },
      });
      if (!obj._petalEvts) obj._petalEvts = [];
      obj._petalEvts.push(orbitEvt);
      obj._petals = obj._petals || [];
      obj._petals.push(petal);
    }

    // Italic label underneath
    obj.label = this.add.text(x, y + 38, def.name, {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '13px',
      fontStyle: 'italic',
      color: '#f4ecd8',
      stroke: '#0a0612',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(23).setAlpha(0.85);

    // Generous touch target (60px radius) — press and hold
    obj.hit = this.add.circle(x, y, 60, 0x000000, 0).setDepth(24).setInteractive({ useHandCursor: true });
    obj.hit.on('pointerdown', (pointer, lx, ly, event) => {
      event.stopPropagation();
      this._startHold(def.id);
    });
    obj.hit.on('pointerup', () => this._endHold(def.id, true));
    obj.hit.on('pointerout', () => this._endHold(def.id, true));
    obj.hit.on('pointerupoutside', () => this._endHold(def.id, true));

    return obj;
  }

  // ── Mailbox ─────────────────────────────────────────────────

  _createMailbox(w, h) {
    // Place it on the visible path area, clear of the painted ivy walls.
    // Inner-left, same height as the rose orb so it's part of the scene.
    const mx = w * 0.16;
    const my = h * 0.70;

    const obj = {};
    obj.x = mx;
    obj.y = my;

    // Warm ambient ground glow so the mailbox reads on the dark painting
    const groundGlow = this.add.circle(mx, my + 22, 44, 0xfff4a0, 0.12).setDepth(24);
    this.tweens.add({
      targets: groundGlow, alpha: { from: 0.08, to: 0.18 },
      duration: 2400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
    obj.groundGlow = groundGlow;

    // Wooden post — lighter wood color for contrast
    const postH = 42;
    const postW = 5;
    obj.post = this.add.rectangle(mx, my + 18, postW, postH, 0x8a6848).setDepth(25);
    obj.post.setStrokeStyle(1, 0x0a0612, 0.8);

    // Mailbox body — bigger, lighter wood, warm parchment accent
    const boxW = 48;
    const boxH = 30;
    const g = this.add.graphics().setDepth(26);
    // Warm outer shadow
    g.fillStyle(0x0a0612, 0.55);
    g.fillRoundedRect(mx - boxW / 2 + 2, my - boxH / 2 - 4 + 2, boxW, boxH, { tl: 14, tr: 14, bl: 3, br: 3 });
    // Body — warm mid-brown
    g.fillStyle(0x7a5a38, 1);
    g.fillRoundedRect(mx - boxW / 2, my - boxH / 2 - 4, boxW, boxH, { tl: 14, tr: 14, bl: 3, br: 3 });
    // Highlight on top arch
    g.fillStyle(0xa07848, 0.8);
    g.fillRoundedRect(mx - boxW / 2 + 2, my - boxH / 2 - 2, boxW - 4, 6, { tl: 12, tr: 12, bl: 0, br: 0 });
    // Bright warm outline
    g.lineStyle(2, 0xfff4a0, 0.95);
    g.strokeRoundedRect(mx - boxW / 2, my - boxH / 2 - 4, boxW, boxH, { tl: 14, tr: 14, bl: 3, br: 3 });
    // Door line
    g.lineStyle(1, 0x3a2414, 0.9);
    g.beginPath();
    g.moveTo(mx - boxW / 2 + 4, my + boxH / 2 - 10);
    g.lineTo(mx + boxW / 2 - 4, my + boxH / 2 - 10);
    g.strokePath();
    // Tiny keyhole
    g.fillStyle(0x1a0e0a, 1);
    g.fillCircle(mx, my + 2, 1.8);
    // Little red flag folded up on the side (classic mailbox)
    g.fillStyle(0xe04838, 1);
    g.fillRect(mx + boxW / 2 - 1, my - boxH / 2 - 6, 2, 10);
    g.fillStyle(0xe04838, 0.95);
    g.fillTriangle(mx + boxW / 2 + 1, my - boxH / 2 - 6, mx + boxW / 2 + 9, my - boxH / 2 - 2, mx + boxW / 2 + 1, my - boxH / 2 + 2);
    g.lineStyle(0.8, 0x0a0612, 0.9);
    g.strokeTriangle(mx + boxW / 2 + 1, my - boxH / 2 - 6, mx + boxW / 2 + 9, my - boxH / 2 - 2, mx + boxW / 2 + 1, my - boxH / 2 + 2);
    obj.graphics = g;

    // Hovering firefly above the mailbox that brightens when there's an unread letter
    obj.firefly = this.add.circle(mx + 18, my - 22, 2.5, 0xfff4a0, 0.9).setDepth(27);
    obj.fireflyHalo = this.add.circle(mx + 18, my - 22, 7, 0xfff4a0, 0.35).setDepth(26);

    // Idle drift
    this.tweens.add({
      targets: [obj.firefly, obj.fireflyHalo],
      x: mx + 26, y: my - 30,
      duration: 2400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // Unread pulse (bright + rapid) toggled via _onNewLetter
    obj._unreadTween = null;

    // "letters" italic label below the post
    obj.label = this.add.text(mx, my + 44, 'letters', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '13px',
      fontStyle: 'italic',
      color: '#fff4a0',
      stroke: '#0a0612',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(28).setAlpha(0.95);

    // Hit zone — big touch target covering the whole mailbox + post
    obj.hit = this.add.circle(mx, my, 55, 0x000000, 0).setDepth(29).setInteractive({ useHandCursor: true });
    obj.hit.on('pointerdown', (pointer, lx, ly, event) => {
      event.stopPropagation();
      this._openMailbox();
    });

    this._mailbox = obj;

    // Reflect current unread state from persisted letters
    this._refreshMailboxUnreadState();
  }

  _refreshMailboxUnreadState() {
    if (!this._mailbox) return;
    const unread = LetterManager.unreadCount();
    if (this._mailbox._unreadTween) {
      this._mailbox._unreadTween.remove(false);
      this._mailbox._unreadTween = null;
    }
    if (unread > 0) {
      // Bright + fast pulse
      this._mailbox.firefly.setFillStyle(0xfff4a0).setAlpha(1);
      this._mailbox.fireflyHalo.setFillStyle(0xfff4a0).setAlpha(0.5);
      this._mailbox._unreadTween = this.tweens.add({
        targets: this._mailbox.fireflyHalo,
        scale: { from: 0.9, to: 1.8 },
        alpha: { from: 0.5, to: 0.2 },
        duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    } else {
      // Dim idle
      this._mailbox.firefly.setFillStyle(0xd4b08a).setAlpha(0.6);
      this._mailbox.fireflyHalo.setFillStyle(0xd4b08a).setAlpha(0.2);
    }
  }

  _onNewLetter(letter) {
    // Chime + screen-edge firefly lift-off animation
    SFX.tap();
    if (navigator.vibrate) navigator.vibrate(25);

    // Brief notification banner: "A new letter from Grandma"
    const w = this.scale.width;
    const hint = this.add.text(w / 2, 90, 'a new letter from Grandma', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '16px',
      fontStyle: 'italic',
      color: '#fff4a0',
      stroke: '#0a0612',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(90).setAlpha(0);
    this.tweens.add({
      targets: hint, alpha: { from: 0, to: 1 },
      duration: 600, yoyo: true, hold: 2400, ease: 'Sine.easeInOut',
      onComplete: () => hint.destroy(),
    });

    // Refresh the mailbox to show unread state
    this._refreshMailboxUnreadState();
  }

  _openMailbox() {
    if (this.scene.isActive('MailboxScene')) return;
    this.scene.pause();
    this.scene.launch('MailboxScene', { caller: 'GardenScene' });
  }

  // ── Secret 5th plant, grown from a successfully-passed seed ─────────

  _onSeedRooted(x, y) {
    if (this._secretPlantSpawned) return;
    this._secretPlantSpawned = true;
    this._spawnSecretPlantAt(x, y, false);
    // Persist it
    GardenState.increment({
      secretPlant: { nx: x / this.scale.width, ny: y / this.scale.height },
    });
  }

  _spawnSecretPlantAt(x, y, skipLetter) {
    const def = {
      id: 'secret_heart',
      name: 'something grown between you',
      nx: x / this.scale.width,
      ny: y / this.scale.height,
      color: 0xfff4a0,
      memoryId: null,
      isSecret: true,
    };
    this._plants.secret_heart = this._createPlantOrb(def);
    // Override the position to the exact seed landing spot
    const obj = this._plants.secret_heart;
    const shiftX = x - obj.x;
    const shiftY = y - obj.y;
    [obj.halo, obj.glow, obj.core, obj.label, obj.hit].forEach(o => {
      if (o) { o.x += shiftX; o.y += shiftY; }
    });
    obj.x = x; obj.y = y;

    if (skipLetter) return;

    // Live letter from Grandma about this moment
    this.time.delayedCall(2000, () => {
      LetterManager.requestLetter({
        event: 'secret_plant_grown',
        plantId: 'secret_heart',
        plantName: 'the secret plant you grew together',
        plantsSoFar: [...this._plantsTendedThisSession],
        secondsInGarden: Math.floor((Date.now() - this._sessionStart) / 1000),
        seedPassed: true,
        isSecret: true,
      });
    });

    const hint = this.add.text(this.scale.width / 2, 130, 'something new has taken root', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '18px',
      fontStyle: 'italic',
      color: '#fff4a0',
      stroke: '#0a0612',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(90).setAlpha(0);
    this.tweens.add({
      targets: hint, alpha: { from: 0, to: 1 },
      duration: 800, yoyo: true, hold: 3000, ease: 'Sine.easeInOut',
      onComplete: () => hint.destroy(),
    });
  }

  _reapplyBloomVisuals() {
    // Swap any already-spawned plant orbs to their bloomed (gold) visual
    for (const id of this._bloomed) {
      const p = this._plants[id];
      if (!p || p._bloomVisualApplied) continue;
      p._bloomVisualApplied = true;
      // Re-tint halo / glow / core / petals
      try { p.halo?.setFillStyle(0xfff4a0, 0.12); } catch (_) {}
      try { p.glow?.setFillStyle(0xfff4a0, 0.35); } catch (_) {}
      if (p._petals) {
        for (const petal of p._petals) { try { petal.setFillStyle(0xfff4a0, 0.9); } catch (_) {} }
      }
    }
  }

  _pulseOrb(plant) {
    SFX.tend();
    if (navigator.vibrate) navigator.vibrate(15);
    this.tweens.add({
      targets: [plant.glow, plant.core],
      scale: { from: 1, to: 1.8 },
      duration: 320, yoyo: true, ease: 'Cubic.easeOut',
    });
    this.tweens.add({
      targets: plant.halo,
      scale: { from: 1, to: 2.2 },
      alpha: { from: 0.25, to: 0.05 },
      duration: 700, yoyo: true, ease: 'Cubic.easeOut',
    });
  }

  // ── Players ─────────────────────────────────────────────────

  _spawnPlayer(playerId, color, x, y) {
    const obj = { color };
    obj.aura = this.add.circle(x, y, 22, color, 0.18).setDepth(30);
    obj.body = this.add.circle(x, y, 10, color, 0.95).setDepth(31).setStrokeStyle(2, 0xf4ecd8, 0.9);
    obj.core = this.add.circle(x, y - 2, 4, 0xf4ecd8, 1).setDepth(32);
    obj.x = x; obj.y = y;
    this.tweens.add({
      targets: obj.aura, scale: { from: 0.9, to: 1.2 }, alpha: { from: 0.18, to: 0.32 },
      duration: 1800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
    this._players.set(playerId, obj);
    return obj;
  }

  _walkPlayerTo(p, tx, ty) {
    const dist = Phaser.Math.Distance.Between(p.x, p.y, tx, ty);
    const duration = Math.min(1800, Math.max(200, dist * 3.5));
    this.tweens.killTweensOf([p.body, p.aura, p.core]);
    this.tweens.add({
      targets: [p.body, p.aura], x: tx, y: ty,
      duration, ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: p.core, x: tx, y: ty - 2, duration, ease: 'Sine.easeInOut',
      onComplete: () => { p.x = tx; p.y = ty; },
    });
    // Re-pulse aura
    this.tweens.add({
      targets: p.aura, scale: { from: 0.9, to: 1.2 }, alpha: { from: 0.18, to: 0.32 },
      duration: 1800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
  }

  _onPointerDown(pointer) {
    // If the pointer went down on any interactive object (plant hit zone,
    // mailbox, seed), those handlers already fired and we shouldn't begin a
    // gesture/walk. Check the top-level target.
    if (pointer.event && pointer.event.target && pointer.event.target !== this.game.canvas) {
      // defensive: we're using Phaser's input plugin which handles this internally
    }
    // Record the down point; decide tap-vs-gesture on move/up
    this._pendingPointer = { x: pointer.x, y: pointer.y, t: Date.now(), dragged: false };
    // Start a potential gesture stroke (it only commits to a gesture if the
    // drag threshold is crossed)
    this._gestureSystem.beginStroke(pointer.x, pointer.y);
  }

  _onPointerMove(pointer) {
    if (!pointer.isDown || !this._pendingPointer) return;
    const moved = Math.hypot(pointer.x - this._pendingPointer.x, pointer.y - this._pendingPointer.y);
    if (moved >= 20) this._pendingPointer.dragged = true;
    this._gestureSystem.continueStroke(pointer.x, pointer.y);
  }

  _onPointerUp(pointer) {
    const gestureResult = this._gestureSystem.endStroke();
    const pending = this._pendingPointer;
    this._pendingPointer = null;
    // If it was a tap (no drag), walk there
    if (!gestureResult.wasGesture && pending && !pending.dragged) {
      this._walkPlayerTo(this._myPlayer, pointer.x, pointer.y);
      Transport.send('move', { nx: pointer.x / this.scale.width, ny: pointer.y / this.scale.height });
    }
  }

  // ── Tend + memory ───────────────────────────────────────────

  // ── Hold-to-tend mechanic ───────────────────────────────────
  // Both players must press and HOLD on a plant simultaneously for 5 seconds.
  // Each player has a progress ring around their hand. When both rings are
  // full at the same time, the memory unlocks. Letting go drains your ring.

  HOLD_DURATION_MS = 5000;

  _startHold(plantId) {
    const plant = this._plants[plantId];
    if (!plant) return;
    // Walk to the plant (quick)
    this._walkPlayerTo(this._myPlayer, plant.x, plant.y + 20);
    // Begin my local hold
    if (plant.localHoldStart) return; // already holding
    plant.localHoldStart = Date.now();
    plant.localHoldProgress = 0;
    this._startHoldRing(plant);
    this._pulseOrb(plant);
    // Broadcast to the other player
    Transport.send('hold_start', { plantId });
    Transport.send('move', { nx: plant.x / this.scale.width, ny: (plant.y + 20) / this.scale.height });
    // Haptic pulse every ~400ms while holding
    plant._hapticTick = this.time.addEvent({
      delay: 400, loop: true,
      callback: () => { if (navigator.vibrate) navigator.vibrate(10); },
    });
  }

  _endHold(plantId, aborted = true) {
    const plant = this._plants[plantId];
    if (!plant || !plant.localHoldStart) return;
    plant.localHoldStart = null;
    plant.localHoldProgress = 0;
    if (plant._hapticTick) { plant._hapticTick.remove(); plant._hapticTick = null; }
    if (aborted) {
      // Drain the ring visually
      this._drainHoldRing(plant);
      Transport.send('hold_end', { plantId });
    }
  }

  _startHoldRing(plant) {
    if (plant._localRing) plant._localRing.destroy();
    if (plant._remoteRing) plant._remoteRing.destroy();
    // Local ring — gold, on our side (slightly offset)
    plant._localRing = this.add.graphics().setDepth(33);
    plant._remoteRing = this.add.graphics().setDepth(33);
  }

  _updateHoldRings(plant) {
    if (!plant._localRing) return;
    const redrawRing = (g, progress, color, innerR) => {
      g.clear();
      if (progress <= 0) return;
      const r = innerR;
      const startA = -Math.PI / 2;
      const endA = startA + Math.PI * 2 * progress;
      // Arc
      g.lineStyle(3, color, 0.9);
      g.beginPath();
      g.arc(plant.x, plant.y, r, startA, endA, false);
      g.strokePath();
      // Inner soft glow
      g.lineStyle(8, color, 0.18);
      g.beginPath();
      g.arc(plant.x, plant.y, r, startA, endA, false);
      g.strokePath();
    };
    redrawRing(plant._localRing, plant.localHoldProgress || 0, 0xffd54f, 34);
    redrawRing(plant._remoteRing, plant.remoteHoldProgress || 0, 0xff80ab, 40);
  }

  _drainHoldRing(plant) {
    if (!plant._localRing) return;
    const ring = plant._localRing;
    const start = plant.localHoldProgress || 0;
    this.tweens.addCounter({
      from: start, to: 0, duration: 400,
      onUpdate: (tw) => {
        plant.localHoldProgress = tw.getValue();
        this._updateHoldRings(plant);
      },
      onComplete: () => {
        if (ring && !ring._checkFired) { try { ring.clear(); } catch (_) {} }
      },
    });
  }

  _checkDualHold(plant) {
    if (this._memoryStarting) return;
    const local = plant.localHoldProgress || 0;
    const remote = plant.remoteHoldProgress || 0;
    // Fire when both rings are nearly full
    if (local > 0.96 && remote > 0.96) {
      this._triggerMemory(plant);
    }
  }

  _triggerMemory(plant) {
    if (this._memoryStarting) return;
    this._memoryStarting = true;
    const wasAlreadyBloomed = this._bloomed.has(plant.def.id);
    this._bloomed.add(plant.def.id); // mark as seen
    this._plantsTendedThisSession.push(plant.def.name);

    // Persist to server — bloom if new, tend count always
    if (wasAlreadyBloomed) {
      GardenState.increment({ tendPlant: plant.def.id });
    } else {
      GardenState.increment({ bloomPlant: plant.def.id });
    }

    // Secret plant path: no pre-written memory — the plant just blooms fully
    // in place, the grandmother writes a live letter about it, and we stay
    // in the garden instead of transitioning to MemoryScene.
    if (plant.def.isSecret) {
      Transport.send('memory_start', { plantId: plant.def.id, memoryId: null });
      this._secretPlantBloom(plant);
      this.time.delayedCall(2000, () => {
        LetterManager.requestLetter({
          event: 'secret_plant_tended',
          plantId: plant.def.id,
          plantName: plant.def.name,
          plantsSoFar: [...this._plantsTendedThisSession],
          secondsInGarden: Math.floor((Date.now() - this._sessionStart) / 1000),
          seedPassed: true,
          isSecret: true,
        });
      });
      // Reset _memoryStarting after a short cooldown so the player can do other things
      this.time.delayedCall(4000, () => { this._memoryStarting = false; });
      return;
    }

    Transport.send('memory_start', { plantId: plant.def.id, memoryId: plant.def.memoryId });
    this._lightShowAndCutscene(plant);
    // Request a fresh letter from Grandma — only the initiator asks, the
    // server broadcasts the result to the whole room via the __letter relay.
    // Delay slightly so the letter arrives during or after the memory ends.
    this.time.delayedCall(3000, () => {
      this._requestLetterForPlant(plant, wasAlreadyBloomed);
    });
  }

  _secretPlantBloom(plant) {
    // In-place bloom — no scene transition. Petal burst + beam + soft shake.
    SFX.memoryUnlock();
    SFX.burst();
    if (navigator.vibrate) navigator.vibrate([30, 60, 30, 80]);

    for (let i = 0; i < 24; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 60 + Math.random() * 120;
      const p = this.add.circle(plant.x, plant.y, 3 + Math.random() * 3, 0xfff4a0, 1).setDepth(40);
      this.tweens.add({
        targets: p,
        x: plant.x + Math.cos(angle) * dist,
        y: plant.y + Math.sin(angle) * dist - 40,
        alpha: 0, scale: 0.3,
        duration: 1500 + Math.random() * 500,
        ease: 'Quad.easeOut',
        onComplete: () => p.destroy(),
      });
    }
    // Plant glow grows permanently
    this.tweens.add({
      targets: [plant.halo],
      scale: { from: 1, to: 1.6 },
      alpha: { from: 0.2, to: 0.45 },
      duration: 1200, ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: [plant.glow, plant.core],
      scale: { from: 1, to: 1.4 },
      duration: 1200, yoyo: true, ease: 'Sine.easeInOut',
    });
    this.cameras.main.shake(400, 0.004);
    this.cameras.main.flash(300, 255, 244, 160);
  }

  _requestLetterForPlant(plant, wasAlreadyBloomed) {
    const secondsInGarden = Math.floor((Date.now() - this._sessionStart) / 1000);
    const context = {
      event: wasAlreadyBloomed ? 'retended_plant' : 'tended_plant',
      plantId: plant.def.id,
      plantName: plant.def.name,
      plantsSoFar: [...this._plantsTendedThisSession],
      visitNumber: 1, // TODO: persist across real sessions
      secondsInGarden,
      isFirstEver: this._plantsTendedThisSession.length === 1,
      retendingBloomedPlant: wasAlreadyBloomed,
      tendedPatiently: secondsInGarden > 40,
      tendedInSilence: secondsInGarden < 15,
    };
    LetterManager.requestLetter(context);
  }

  _lightShowAndCutscene(plant) {
    const w = this.scale.width;
    const h = this.scale.height;
    const px = plant.x;
    const py = plant.y;

    SFX.memoryUnlock();
    SFX.burst();
    if (navigator.vibrate) navigator.vibrate([30, 60, 100]);

    Audio.playMusic('memory_unfold', { fadeIn: 1500, fadeOut: 800, volume: 0.5 });

    // Orb explodes
    this.tweens.add({
      targets: [plant.glow, plant.core], scale: 5, alpha: 0,
      duration: 700, ease: 'Cubic.easeOut',
    });
    this.tweens.add({
      targets: plant.halo, scale: 7, alpha: 0,
      duration: 900, ease: 'Cubic.easeOut',
    });

    // Petal burst — 36 outward
    for (let i = 0; i < 36; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 80 + Math.random() * 220;
      const petal = this.add.circle(px, py, 3 + Math.random() * 4, plant.def.color, 1).setDepth(40);
      this.tweens.add({
        targets: petal,
        x: px + Math.cos(angle) * dist,
        y: py + Math.sin(angle) * dist - 80,
        alpha: 0, scale: 0.3,
        duration: 1500 + Math.random() * 800,
        ease: 'Quad.easeOut',
        onComplete: () => petal.destroy(),
      });
    }

    // Light beam from above
    const beam = this.add.graphics().setDepth(38);
    this.tweens.addCounter({
      from: 0, to: 0.6, duration: 400,
      onUpdate: (tw) => {
        beam.clear();
        beam.fillStyle(0xfff4a0, tw.getValue());
        beam.fillTriangle(px - 50, 0, px + 50, 0, px, py + 30);
      },
    });

    // Lightning strikes
    for (let i = 0; i < 6; i++) {
      this.time.delayedCall(200 + i * 80, () => {
        this._drawLightning(px + (Math.random() - 0.5) * 250, py);
        SFX.lightning();
      });
    }

    // Screen shake + flash
    this.cameras.main.shake(700, 0.008);
    this.time.delayedCall(400, () => this.cameras.main.flash(400, 255, 244, 160));

    // Aurora wash
    this.time.delayedCall(600, () => this._auroraWash());

    // Transition to memory
    this.time.delayedCall(2800, () => {
      this.cameras.main.fadeOut(800, 255, 244, 160);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scale.off('resize', this._onResize, this);
        this.scene.start('MemoryScene', {
          memoryId: plant.def.memoryId,
          plantId: plant.def.id,
          roomCode: this.roomCode,
          role: this.role,
        });
      });
    });
  }

  _drawLightning(targetX, targetY) {
    const startX = targetX + (Math.random() - 0.5) * 60;
    const segments = 8;
    const points = [];
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      points.push({
        x: Phaser.Math.Linear(startX, targetX, t) + (Math.random() - 0.5) * 30,
        y: Phaser.Math.Linear(0, targetY, t),
      });
    }
    const bolt = this.add.graphics().setDepth(45);
    bolt.lineStyle(3, 0xfff4a0, 1);
    bolt.beginPath();
    bolt.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) bolt.lineTo(points[i].x, points[i].y);
    bolt.strokePath();
    const glow = this.add.graphics().setDepth(44);
    glow.lineStyle(8, 0xfff4a0, 0.4);
    glow.beginPath();
    glow.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) glow.lineTo(points[i].x, points[i].y);
    glow.strokePath();
    this.tweens.add({
      targets: [bolt, glow], alpha: 0, duration: 250, ease: 'Cubic.easeOut',
      onComplete: () => { bolt.destroy(); glow.destroy(); },
    });
  }

  _auroraWash() {
    const w = this.scale.width;
    const h = this.scale.height;
    const colors = [0xff80ab, 0xfff4a0, 0x80c8ff, 0xb088ff];
    colors.forEach((c, i) => {
      const wave = this.add.graphics().setDepth(46);
      this.tweens.addCounter({
        from: 0, to: 0.35, duration: 800, delay: i * 120,
        onUpdate: (tw) => { wave.clear(); wave.fillStyle(c, tw.getValue()); wave.fillRect(0, 0, w, h); },
      });
      this.tweens.addCounter({
        from: 0.35, to: 0, duration: 1000, delay: 800 + i * 120,
        onUpdate: (tw) => { wave.clear(); wave.fillStyle(c, tw.getValue()); wave.fillRect(0, 0, w, h); },
        onComplete: () => wave.destroy(),
      });
    });
  }

  // ── Multiplayer ─────────────────────────────────────────────

  _wireTransport() {
    Transport.on('hello', (payload, fromId) => {
      if (!this._players.has(fromId)) {
        const color = payload.role === 'host' ? 0xffd54f : 0xff80ab;
        this._spawnPlayer(fromId, color, this.scale.width * 0.5, this.scale.height * 0.6);
      }
      Transport.send('hello', { role: this.role });
    });

    Transport.on('move', (payload, fromId) => {
      let p = this._players.get(fromId);
      if (!p) {
        const color = this.role === 'host' ? 0xff80ab : 0xffd54f;
        p = this._spawnPlayer(fromId, color, payload.nx * this.scale.width, payload.ny * this.scale.height);
      }
      this._walkPlayerTo(p, payload.nx * this.scale.width, payload.ny * this.scale.height);
    });

    // Peer started holding a plant
    Transport.on('hold_start', (payload, fromId) => {
      const plant = this._plants[payload.plantId];
      if (!plant) return;
      plant.remoteHoldStart = Date.now();
      plant.remoteHoldProgress = 0;
      this._startHoldRing(plant);
      this._pulseOrb(plant);
    });

    // Peer sent a progress update
    Transport.on('hold_progress', (payload) => {
      const plant = this._plants[payload.plantId];
      if (!plant) return;
      plant.remoteHoldProgress = payload.p || 0;
      this._updateHoldRings(plant);
      this._checkDualHold(plant);
    });

    // Peer released their hold
    Transport.on('hold_end', (payload) => {
      const plant = this._plants[payload.plantId];
      if (!plant) return;
      plant.remoteHoldStart = null;
      const start = plant.remoteHoldProgress || 0;
      // Drain remote ring
      this.tweens.addCounter({
        from: start, to: 0, duration: 400,
        onUpdate: (tw) => {
          plant.remoteHoldProgress = tw.getValue();
          this._updateHoldRings(plant);
        },
      });
    });

    Transport.on('memory_start', (payload) => {
      const plant = this._plants[payload.plantId];
      if (plant && !this._memoryStarting) this._triggerMemory(plant);
    });

    Transport.on('bye', (_, fromId) => {
      const p = this._players.get(fromId);
      if (p) {
        [p.aura, p.body, p.core].forEach(o => o?.destroy());
        this._players.delete(fromId);
      }
    });

    // __letter is a server-originated broadcast (not a peer message), so it
    // gets dispatched by WSTransport with the payload only. Handle it here.
    Transport.on('__letter', (payload) => {
      LetterManager.receiveFromRelay(payload);
    });
  }

  // ── Atmospheric particles ───────────────────────────────────

  _spawnFireflies(count) {
    const w = this.scale.width;
    const h = this.scale.height;
    for (let i = 0; i < count; i++) {
      const x = Math.random() * w;
      const y = h * 0.2 + Math.random() * h * 0.7;
      const ff = this.add.circle(x, y, 1.6 + Math.random() * 1.2, 0xfff4a0, 0.85).setDepth(15);
      const halo = this.add.circle(x, y, 5, 0xfff4a0, 0.25).setDepth(14);
      const drift = () => {
        const tx = x + (Math.random() - 0.5) * 90;
        const ty = y + (Math.random() - 0.5) * 60;
        this.tweens.add({
          targets: [ff, halo], x: tx, y: ty,
          duration: 3500 + Math.random() * 3000, ease: 'Sine.easeInOut',
          onComplete: drift,
        });
      };
      drift();
      this.tweens.add({
        targets: ff, alpha: { from: 0.4, to: 1 },
        duration: 1300 + Math.random() * 1500, yoyo: true, repeat: -1,
      });
      this.tweens.add({
        targets: halo, alpha: { from: 0.05, to: 0.32 }, scale: { from: 0.8, to: 1.4 },
        duration: 1300 + Math.random() * 1500, yoyo: true, repeat: -1,
      });
    }
  }

  _spawnDust(count) {
    const w = this.scale.width;
    const h = this.scale.height;
    for (let i = 0; i < count; i++) {
      const x = Math.random() * w;
      const y = Math.random() * h;
      const d = this.add.circle(x, y, 0.7 + Math.random(), 0xf4ecd8, 0.3 + Math.random() * 0.3).setDepth(12);
      this.tweens.add({
        targets: d,
        y: y - 80 - Math.random() * 100,
        x: x + (Math.random() - 0.5) * 40,
        alpha: { from: d.alpha, to: 0 },
        duration: 8000 + Math.random() * 5000,
        repeat: -1,
        onRepeat: () => { d.x = Math.random() * this.scale.width; d.y = this.scale.height + 10; d.alpha = 0.3 + Math.random() * 0.3; },
      });
    }
  }

  _drawProcBg(w, h) {
    const g = this.add.graphics().setDepth(0);
    g.fillGradientStyle(0x0a0612, 0x0a0612, 0x3a2a4a, 0x1a0e2e, 1, 1, 1, 1);
    g.fillRect(0, 0, w, h);
  }

  // ── HUD ─────────────────────────────────────────────────────

  _buildHUD(w, h) {
    // Top-right corner: tiny room code chip
    const pad = 12;
    const chipW = 88;
    const chipH = 30;
    const cx = w - chipW / 2 - pad;
    const cy = pad + chipH / 2;
    const chipBg = this.add.graphics().setDepth(80);
    chipBg.fillStyle(0x0a0612, 0.7);
    chipBg.fillRoundedRect(cx - chipW / 2, cy - chipH / 2, chipW, chipH, 8);
    chipBg.lineStyle(1, 0xd4b08a, 0.6);
    chipBg.strokeRoundedRect(cx - chipW / 2, cy - chipH / 2, chipW, chipH, 8);
    this.add.text(cx - chipW / 2 + 10, cy, '◆', {
      fontFamily: 'Cormorant Garamond, serif', fontSize: '12px', color: '#d4b08a',
    }).setOrigin(0, 0.5).setDepth(81);
    this.add.text(cx + 4, cy, this.roomCode, {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '16px', color: '#f4ecd8',
    }).setOrigin(0.5, 0.5).setDepth(81).setLetterSpacing(2);

    // Bottom center: subtle hint (only briefly on first load)
    const hint = this.add.text(w / 2, h - 28, 'press and hold a light. together.', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '13px',
      fontStyle: 'italic',
      color: '#f4ecd8',
      stroke: '#0a0612',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(80).setAlpha(0);
    this.tweens.add({
      targets: hint, alpha: { from: 0, to: 0.8 },
      duration: 1200, delay: 800,
    });
    this.tweens.add({
      targets: hint, alpha: 0,
      duration: 1500, delay: 6500,
    });
  }
}
